/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- The horizontally scrollable history region needs a keyboard focus target on narrow screens. */
import type { FC } from "react";
import { Badge, Button, Spinner } from "flowbite-react";
import {
  HiChevronLeft,
  HiChevronRight,
  HiDownload,
  HiExclamation,
  HiRefresh,
} from "react-icons/hi";
import type { ExportJob } from "../../types/exports";

interface ExportHistoryTableProps {
  jobs: ExportJob[];
  loading: boolean;
  error?: string | null;
  page: number;
  hasMore: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onRefresh: () => void;
  onDownload: (job: ExportJob) => void;
  onRetry: (job: ExportJob) => void;
  onDownloadWarnings: (job: ExportJob) => void;
  downloadingJobId?: string | null;
  downloadingWarningsJobId?: string | null;
  retryingJobId?: string | null;
}

const statusDetails = {
  pending: { label: "Ausstehend", color: "gray" },
  running: { label: "Wird erstellt", color: "info" },
  finished_successfully: {
    label: "Erfolgreich abgeschlossen",
    color: "success",
  },
  finished_with_errors: {
    label: "Mit Fehlern abgeschlossen",
    color: "warning",
  },
  error: { label: "Fehlgeschlagen", color: "failure" },
} as const;

const formatDate = (value: string): string => {
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat("de-DE", { timeZone: "UTC" }).format(
    new Date(Date.UTC(year, month - 1, day))
  );
};

const formatEndDate = (exclusiveDate: string): string => {
  const [year, month, day] = exclusiveDate.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return exclusiveDate;
  return new Intl.DateTimeFormat("de-DE", { timeZone: "UTC" }).format(
    new Date(Date.UTC(year, month - 1, day - 1))
  );
};

const formatDateTime = (value: string | null): string =>
  value ? new Date(value).toLocaleString("de-DE") : "–";

const formatCount = (value: number | null): string =>
  value === null ? "–" : value.toLocaleString("de-DE");

const labelsFor = (
  job: ExportJob,
  field: keyof ExportJob["filter_labels"]
): string[] => {
  const raw = job.filter_labels[field];
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const label = (item as { label?: unknown }).label;
    return typeof label === "string" && label.trim() ? [label] : [];
  });
};

const summary = (values: string[], emptyLabel: string): string =>
  values.length ? values.join(", ") : emptyLabel;

const ExportHistoryTable: FC<ExportHistoryTableProps> = ({
  jobs,
  loading,
  error,
  page,
  hasMore,
  onPrevious,
  onNext,
  onRefresh,
  onDownload,
  onRetry,
  onDownloadWarnings,
  downloadingJobId,
  downloadingWarningsJobId,
  retryingJobId,
}) => {
  const actionBusy = Boolean(
    downloadingJobId || downloadingWarningsJobId || retryingJobId
  );

  return (
    <section
      aria-labelledby="export-history-title"
      className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700">
        <div>
          <h2
            id="export-history-title"
            className="text-lg font-semibold text-gray-900 dark:text-white"
          >
            Bisherige Exporte
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Exporte laufen im Hintergrund. Fertige Dateien können Sie später
            erneut herunterladen.
          </p>
        </div>
        <Button
          color="light"
          size="sm"
          type="button"
          onClick={onRefresh}
          disabled={loading}
        >
          <HiRefresh className="mr-2 h-4 w-4" aria-hidden="true" />
          Aktualisieren
        </Button>
      </div>

      <div
        className="overflow-x-auto focus:outline-none focus:ring-2 focus:ring-blue-500"
        role="region"
        aria-label="Exportliste, horizontal scrollbar auf kleinen Bildschirmen"
        tabIndex={0}
      >
        <table className="w-full min-w-[920px] text-left text-sm text-gray-700 dark:text-gray-300">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            <tr>
              <th scope="col" className="px-4 py-3">
                Erstellt
              </th>
              <th scope="col" className="px-4 py-3">
                Zeitraum
              </th>
              <th scope="col" className="px-4 py-3">
                Auswahl
              </th>
              <th scope="col" className="px-4 py-3">
                Status
              </th>
              <th scope="col" className="px-4 py-3">
                Datensätze
              </th>
              <th scope="col" className="px-4 py-3">
                Aktion
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {jobs.map((job) => {
              const providers = labelsFor(job, "providers");
              const types = labelsFor(job, "contract_types");
              const teams = labelsFor(job, "teams");
              const workers = labelsFor(job, "workers");
              const status = statusDetails[job.status];
              const canDownload =
                (job.status === "finished_successfully" ||
                  job.status === "finished_with_errors") &&
                job.file_available;

              return (
                <tr
                  key={job.id}
                  className="align-top hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="px-4 py-4">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {formatDateTime(job.created_at)}
                    </div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {job.requested_by_name || "Unbekannt"}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-4">
                    {formatDate(job.date_from)} –{" "}
                    {formatEndDate(job.date_to_exclusive)}
                  </td>
                  <td className="max-w-xs px-4 py-4">
                    <div
                      className="truncate font-medium text-gray-900 dark:text-white"
                      title={providers.join(", ")}
                    >
                      {summary(providers, "Keine Anbieter")}
                    </div>
                    <details className="mt-1 text-xs">
                      <summary className="cursor-pointer text-blue-700 hover:underline focus-visible:rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 dark:text-blue-300">
                        Filter anzeigen
                      </summary>
                      <dl className="mt-2 space-y-1 break-words rounded-md bg-gray-50 p-2 dark:bg-gray-900">
                        <div>
                          <dt className="font-semibold">Anbieter</dt>
                          <dd>{summary(providers, "Keine Anbieter")}</dd>
                        </div>
                        <div>
                          <dt className="font-semibold">Vertragsarten</dt>
                          <dd>{summary(types, "Alle Vertragsarten")}</dd>
                        </div>
                        <div>
                          <dt className="font-semibold">Teams</dt>
                          <dd>{summary(teams, "Keine Teams")}</dd>
                        </div>
                        <div>
                          <dt className="font-semibold">
                            Einzeln ausgewählte Mitarbeitende
                          </dt>
                          <dd>
                            {summary(
                              workers,
                              job.worker_scope_mode === "all"
                                ? "Alle Mitarbeitenden"
                                : "Keine"
                            )}
                          </dd>
                        </div>
                      </dl>
                    </details>
                  </td>
                  <td className="px-4 py-4">
                    <Badge
                      color={status.color}
                      className="w-fit whitespace-nowrap"
                    >
                      {status.label}
                    </Badge>
                    {job.warning_row_count > 0 &&
                      (job.status === "finished_successfully" ||
                      job.status === "finished_with_errors" ? (
                        <button
                          type="button"
                          onClick={() => onDownloadWarnings(job)}
                          disabled={actionBusy}
                          title="Liste der Verträge mit Datenhinweisen als CSV herunterladen"
                          aria-label={`${formatCount(
                            job.warning_row_count
                          )} Datenhinweise zum Export vom ${formatDateTime(
                            job.created_at
                          )} herunterladen`}
                          className="mt-2 inline-flex items-center gap-1 rounded text-xs font-medium text-amber-700 underline decoration-dotted underline-offset-2 hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-amber-300 dark:hover:text-amber-200"
                        >
                          {downloadingWarningsJobId === job.id ? (
                            <Spinner size="xs" />
                          ) : (
                            <HiDownload
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            />
                          )}
                          {formatCount(job.warning_row_count)} Datenhinweise
                        </button>
                      ) : (
                        <div className="mt-2 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                          <HiExclamation
                            className="h-3.5 w-3.5"
                            aria-hidden="true"
                          />
                          {formatCount(job.warning_row_count)} Datenhinweise
                        </div>
                      ))}
                    {job.status === "error" && job.error_message && (
                      <p
                        className="mt-2 max-w-xs text-xs text-red-700 dark:text-red-300"
                        role="status"
                      >
                        {job.error_message}
                      </p>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4">
                    {formatCount(job.processed_rows)} /{" "}
                    {formatCount(job.row_count)}
                    {job.snapshot_at && (
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Stand: {formatDateTime(job.snapshot_at)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {canDownload ? (
                      <Button
                        color="blue"
                        size="xs"
                        type="button"
                        onClick={() => onDownload(job)}
                        disabled={actionBusy}
                        aria-label={`Export vom ${formatDateTime(
                          job.created_at
                        )} herunterladen`}
                      >
                        {downloadingJobId === job.id ? (
                          <Spinner size="xs" className="mr-2" />
                        ) : (
                          <HiDownload
                            className="mr-2 h-4 w-4"
                            aria-hidden="true"
                          />
                        )}
                        Herunterladen
                      </Button>
                    ) : job.status === "error" ? (
                      <Button
                        color="light"
                        size="xs"
                        type="button"
                        onClick={() => onRetry(job)}
                        disabled={actionBusy}
                        aria-label={`Export vom ${formatDateTime(
                          job.created_at
                        )} erneut erstellen`}
                      >
                        {retryingJobId === job.id && (
                          <Spinner size="xs" className="mr-2" />
                        )}
                        Erneut erstellen
                      </Button>
                    ) : job.status === "finished_successfully" ||
                      job.status === "finished_with_errors" ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Datei nicht verfügbar
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Noch nicht verfügbar
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && !error && jobs.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  Noch keine Exporte vorhanden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 p-4 dark:border-gray-700">
        <span
          className="text-sm text-gray-500 dark:text-gray-400"
          aria-live="polite"
        >
          {loading
            ? "Exporte werden geladen…"
            : `Seite ${page} · ${jobs.length} Exporte`}
        </span>
        <div className="flex gap-2">
          <Button
            color="light"
            size="sm"
            type="button"
            onClick={onPrevious}
            disabled={loading || page <= 1}
          >
            <HiChevronLeft className="mr-1 h-4 w-4" aria-hidden="true" /> Zurück
          </Button>
          <Button
            color="light"
            size="sm"
            type="button"
            onClick={onNext}
            disabled={loading || !hasMore}
          >
            Weiter{" "}
            <HiChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ExportHistoryTable;
